/**
 * Caret coordinate utilities
 * Uses the mirror-element technique to compute pixel coordinates
 * of the cursor inside a <textarea>.
 */

interface CaretCoordinates {
  readonly top: number
  readonly left: number
  readonly height: number
}

/**
 * Properties of a textarea that affect text layout.
 * Copied onto the mirror div so it renders identically.
 */
const MIRROR_PROPERTIES = [
  'direction',
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
  'MozTabSize',
  'whiteSpace',
  'wordWrap',
  'wordBreak',
] as const

/**
 * Compute the pixel coordinates of the caret at `position` inside
 * the given textarea element, relative to the textarea's top-left corner.
 *
 * Works by creating an offscreen mirror `<div>` that exactly replicates
 * the textarea's text layout, inserting the text up to `position`, and
 * measuring the final inline element.
 */
export function getCaretCoordinates(
  element: HTMLTextAreaElement,
  position: number
): CaretCoordinates {
  const div = document.createElement('div')
  div.id = 'caret-mirror'
  document.body.appendChild(div)

  const style = div.style
  const computed = window.getComputedStyle(element)

  style.whiteSpace = 'pre-wrap'
  style.wordWrap = 'break-word'
  style.position = 'absolute'
  style.visibility = 'hidden'
  style.overflow = 'hidden'

  for (const prop of MIRROR_PROPERTIES) {
    style.setProperty(prop, computed.getPropertyValue(prop))
  }

  div.textContent = element.value.substring(0, position)

  const span = document.createElement('span')
  // Use zero-width space so it has height but no width
  span.textContent = element.value.substring(position) || '\u200b'
  div.appendChild(span)

  const coordinates: CaretCoordinates = {
    top: span.offsetTop - element.scrollTop,
    left: span.offsetLeft - element.scrollLeft,
    height: parseInt(computed.lineHeight, 10) || span.offsetHeight,
  }

  document.body.removeChild(div)

  return coordinates
}
